import { useEffect, useState } from "react";
import { getMarketingQuestions } from "../api/diagnosticsApi";

export default function MarketingForm({ onSubmit, isAuthenticated }: { onSubmit: (answers: any) => void, isAuthenticated: boolean; }) {
  const [questions, setQuestions] = useState<any[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const questionsPerPage = 6;

  useEffect(() => {
    getMarketingQuestions().then(setQuestions);
  }, []);

  function handleChange(id: string, value: string) {
    setAnswers(prev => ({ ...prev, [id]: value }));
  }

  function nextQuestion() {
    if (currentIndex + questionsPerPage < questions.length) {
      setCurrentIndex(prev => prev + questionsPerPage);
    }
  }

  function prevQuestion() {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - questionsPerPage);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit(answers);
  }

  if (questions.length === 0) return <p>Loading questions...</p>;

  const currentQuestions = questions.slice(currentIndex, currentIndex + questionsPerPage);
  const isLastPage = currentIndex + questionsPerPage >= questions.length;
  const isCurrentSetComplete = currentQuestions.every(q => (answers[q.id] || "").trim() !== "");
  const answeredQuestions = questions.filter((q) => {
    const answer = answers[q.id];

    return (
      answer !== undefined &&
      answer !== null &&
      answer.trim() !== ""
    );
  }).length;

  const completionPercentage =
    questions.length === 0
      ? 0
      : Math.round(
          (answeredQuestions / questions.length) * 100
        );

  const progressMessage =
  completionPercentage >= 100
    ? {
        heading: "You're ready to submit",
        body: "You've completed the marketing assessment. Submit your answers to generate your diagnostic report.",
      }
    : completionPercentage >= 50
      ? {
          heading: "You're making great progress",
          body: "Keep going. You're building a clearer picture of your marketing maturity and performance.",
        }
      : {
          heading: "Let's assess your marketing performance",
          body: "Answer the questions below to identify strengths, gaps, and opportunities across your marketing activities.",
        };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 mb-20">
      <div className="rounded-3xl border border-strivre-lightnavy bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500 text-left">
              Marketing Assessment
            </p>

            <h3 className="text-lg font-semibold text-slate-900 text-left">
              Answer the questions below to build a complete picture of your marketing performance.
            </h3>
          </div>

          <div className="text-sm text-slate-500">
          <div>
            Questions {currentIndex + 1} -{" "}
            {Math.min(
              currentIndex + questionsPerPage,
              questions.length
            )}{" "}
            of {questions.length}
          </div>

          <div>
            {answeredQuestions} of {questions.length} answered
            {" · "}
            {completionPercentage}% Complete
          </div>
        </div>
        </div>

        <div className="mt-4 h-2 rounded-full bg-strivre-softgrey">
          <div
            className="h-2 rounded-full bg-strivre-burgundy transition-all"
            style={{
              width: `${completionPercentage}%`,
            }}
          />
        </div>

        <div className="mt-4 rounded-2xl border border-strivre-lightburgundy bg-strivre-lightburgundy p-4 text-sm text-slate-700">
          <p className="font-semibold text-slate-900">
            {progressMessage.heading}
          </p>

          <p className="mt-1">
            {progressMessage.body}
          </p>
        </div>
      </div>
      
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {currentQuestions.map((q, idx) => {
          const currentAnswer = answers[q.id] || "";

          return (
            <div
              key={q.id}
              className="bg-white p-6 border border-strivre-lightnavy rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 animate-fade-in h-full"
              style={{ animationDelay: `${idx * 100}ms` }}
            >
              <label className="block text-lg font-semibold text-slate-900 mb-3">
                {q.label}
              </label>

              {q.type === "select" && q.options ? (
                <select
                  value={currentAnswer}
                  onChange={(e) => handleChange(q.id, e.target.value)}
                  className="input-base px-3 focus:ring-offset-0"
                  required
                >
                  <option value="" disabled>
                    {q.placeholder}
                  </option>

                  {q.options.map((opt: string) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              ) : q.type === "textarea" ? (
                <textarea
                  placeholder={q.placeholder}
                  value={currentAnswer}
                  onChange={(e) => handleChange(q.id, e.target.value)}
                  className="input-base px-3 focus:ring-offset-0 min-h-[120px]"
                  required
                />
              ) : (
                <input
                  type={q.type}
                  placeholder={q.placeholder}
                  value={currentAnswer}
                  onChange={(e) => handleChange(q.id, e.target.value)}
                  className="input-base px-3 focus:ring-offset-0"
                  required
                />
              )}
            </div>
          );
        })}
      </div>
      </div>
      {isLastPage && (
        <div className={isAuthenticated ? "w-full" : "max-w-7xl mx-auto"}>
          <div className="rounded-2xl border border-strivre-lightnavy bg-strivre-lightburgundy p-4">
            <p className="mb-2 text-sm font-semibold text-strivre-navy">
              Privacy Notice
            </p>

            <p className="text-sm text-slate-700 leading-relaxed">
              By submitting this form, you confirm that the information provided is
              accurate and agree that Strivre Services and/or the relevant organisation
              may contact you regarding your enquiry, request, or submitted information.
              Your information will be handled in accordance with our Privacy Policy and
              will not be used for purposes unrelated to your enquiry without your
              consent.
            </p>
          </div>
        </div>
      )}
      <div className="flex flex-wrap justify-between pt-4">
        <button
          type="button"
          onClick={prevQuestion}
          disabled={currentIndex === 0}
          className="btn-secondary btn-md shrink-0"
        >
          Previous
        </button>

        {!isLastPage ? (
          <button
            type="button"
            onClick={nextQuestion}
            disabled={!isCurrentSetComplete}
            className="btn-primary btn-md shrink-0"
          >
            Next
          </button>
        ) : (
          <button
            type="submit"
            disabled={!isCurrentSetComplete}
            className="btn-primary btn-md shrink-0"
          >
            Submit
          </button>
        )}
      </div>      
    </form>
  );
}